import { useEffect } from "react";
import Lenis from "lenis";

/**
 * Smooth scroll powered by Lenis.
 * - Disabled on touch devices (native scroll feels better there).
 * - Respects prefers-reduced-motion.
 */
export function useLenis() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const isTouch =
      window.matchMedia("(hover: none)").matches ||
      window.matchMedia("(pointer: coarse)").matches;
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (isTouch || reducedMotion) return;

    let lenis: Lenis | null = null;
    let rafId = 0;
    let cancelled = false;

    // Defer Lenis init until the browser is idle so it doesn't block
    // initial render / TTI. Falls back to setTimeout where requestIdleCallback
    // is unavailable (Safari).
    const idle =
      (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
        .requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 200));

    const handle = idle(() => {
      if (cancelled) return;
      lenis = new Lenis({
        duration: 1.1,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 1.2,
      });
      const raf = (time: number) => {
        lenis?.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    });

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      const cancelIdle = (
        window as unknown as { cancelIdleCallback?: (h: number) => void }
      ).cancelIdleCallback;
      if (cancelIdle && typeof handle === "number") cancelIdle(handle);
      lenis?.destroy();
    };
  }, []);
}
